import {
  checkIcon,
  ExpressionScopeRequestPayload,
  html,
  icon,
  identifierRegex,
  numberFormat,
  parseAndValidateReportExpression,
  render,
  ReportEntityType,
  ReportRowsLoadedPayload,
  ReportSharedWith
} from '@cumu/shared';
import { target, targets } from '@github/catalyst';
import { parseReportParameter } from 'shared/src/expressions/report-parameters';
import { allFormData } from '../forms/utilities';
import { validateForm } from '../forms/validation';
import { parseReportDefinition } from '../report-definition';
import { isSessionExpiredResponse } from '../session';
import { controller } from './controller';

@controller('report-configuration')
class ReportConfigurationElement extends HTMLElement {
  @target declare form: HTMLFormElement;
  @target declare previewFields: HTMLElement;
  @target declare previewContainer: HTMLElement;
  @targets declare columnSortSelects: HTMLSelectElement[];
  @target declare sharesFormGroup: HTMLElement;
  previewTimeout = 0;
  previewController: AbortController | undefined = undefined;

  connectedCallback() {
    if (this.previewContainer) {
      this.preview();
    }

    this.addEventListener('details-menu-selected', this, true);
  }

  disconnectedCallback() {
    this.removeEventListener('details-menu-selected', this, true);
  }

  handleEvent(event: Event) {
    switch (event.type) {
      case 'details-menu-selected':
        this.enqueuePreview(event);
        if (
          event.target instanceof Element &&
          event.target.querySelector(
            '[name^="is_shared:"],[name^="project_id"]'
          )
        ) {
          this.handleSharedWithChanged(
            !!event.target.querySelector('[name^="is_shared:"]')
          );
        }
        break;
    }
  }

  get type() {
    return this.getAttribute('entity-type') as ReportEntityType;
  }

  get organization_id() {
    return this.getAttribute('organization-id')!;
  }

  get previewStatusContainer() {
    return document.getElementById(this.getAttribute('preview-status')!)!;
  }

  validateConfiguration() {
    return validateForm(this.form, true, this);
  }

  setLoadingPreview() {
    this.previewContainer.innerHTML = `<h2 class="p-3"><span>Loading</span><span class="AnimatedEllipsis"></span></h2>`;
    this.previewStatusContainer.innerHTML = `<span class="AnimatedEllipsis"></span>`;
    this.querySelectorAll('output[report-parameter-value-output]').forEach(
      el =>
        (el.innerHTML = `<span>Loading</span><span class="AnimatedEllipsis"></span>`)
    );
  }

  setPreviewValidationError() {
    this.previewContainer.innerHTML = render(html`
      <h2 class="p-3">Validation error</h2>
    `);
    this.previewStatusContainer.textContent = '0';
    this.querySelectorAll('output[report-parameter-value-output]').forEach(
      el => (el.innerHTML = '')
    );
  }

  setPreviewServerError(sessionExpired: boolean) {
    this.previewContainer.innerHTML = render(html`
      <h2 class="p-3">
        ${sessionExpired ? 'Session expired' : 'Error loading report preview.'}
      </h2>
    `);
    this.previewStatusContainer.textContent = '0';
    this.querySelectorAll('output[report-parameter-value-output]').forEach(
      el => (el.innerHTML = '')
    );
  }

  setPreviewResults(fragment: string) {
    this.previewContainer.innerHTML = fragment;
    const parameter_values = this.previewContainer
      .querySelector('dashboard-controller[parameter_values]')
      ?.getAttribute('parameter_values');
    if (parameter_values) {
      this.previewStatusContainer.innerHTML = render(
        icon(checkIcon, 'color-fg-success')
      );
      this.previewParameterValues(JSON.parse(parameter_values));
    }
  }

  onReportGridRowsLoaded(event: CustomEvent<ReportRowsLoadedPayload>) {
    if (
      event.target instanceof Element &&
      event.target.closest('dashboard-controller')
    ) {
      // ignore events originating from a dashboard grid.
      return;
    }

    const { count, parameter_values } = event.detail;
    this.previewStatusContainer.textContent = numberFormat.format(count);
    this.previewParameterValues(parameter_values);
  }

  private previewParameterValues(
    parameter_values: Record<string, string | number | null>
  ) {
    this.querySelectorAll('output[report-parameter-value-output]').forEach(
      el => {
        el.innerHTML = '';
        const li = el.closest('li');
        const name = li?.querySelector<HTMLInputElement>(
          'input[name$="/name"]'
        )?.value;
        if (name && name in parameter_values) {
          el.innerHTML = parameter_values[name]?.toString() ?? 'NULL';
        }
      }
    );
  }

  enqueuePreview(event: Event) {
    if (
      event.target instanceof Element &&
      event.target.closest('form') !== this.form
    ) {
      return;
    }
    clearTimeout(this.previewTimeout);
    this.previewTimeout = setTimeout(() => this.preview(), 300);
  }

  async preview() {
    clearTimeout(this.previewTimeout);
    const { valid } = await validateForm(this.form, false, this.previewFields);
    if (!valid) {
      return;
    }
    if (this.previewController) {
      this.previewController.abort('superseded');
    }
    this.setLoadingPreview();
    const data = allFormData(this.form);
    data.set('title', 'Preview');
    const body = new URLSearchParams(data as any);
    this.previewController = new AbortController();
    const response = await fetch(
      `/api/organizations/${this.organization_id}/reports/preview`,
      {
        method: 'POST',
        body,
        signal: this.previewController.signal
      }
    );
    this.previewController = undefined;
    if (response.ok) {
      const fragment = await response.text();
      this.setPreviewResults(fragment);
    } else if (response.status === 400) {
      this.setPreviewValidationError();
    } else {
      this.setPreviewServerError(isSessionExpiredResponse(response));
    }
  }

  columnSortChanged(event: Event) {
    const target = event.target as HTMLSelectElement;
    for (const select of this.columnSortSelects) {
      if (
        select !== target &&
        select.value[0] >= (target.value[0] ?? '0') &&
        select.value !== ''
      ) {
        select.value = '';
      }
    }
    updateColumnSortOptions(target, this.columnSortSelects);
  }

  columnSortFocused(event: Event) {
    const target = event.target as HTMLSelectElement;
    updateColumnSortOptions(target, this.columnSortSelects);
  }

  handleExpressionScopeRequest(
    event: CustomEvent<ExpressionScopeRequestPayload>
  ) {
    event.preventDefault();
    const {
      definition: { parameters },
      properties
    } = parseReportDefinition(this.form);
    for (const parameter of parameters) {
      if (!identifierRegex.test(parameter.name)) {
        continue;
      }
      const result = parseReportParameter({
        parameter,
        properties,
        parseExpression: expression =>
          parseAndValidateReportExpression({
            name: parameter.name,
            expression,
            getDefinitionInfo: () => ({ columns: [], parameters, properties })
          })
      });
      if (result.valid) {
        event.detail.identifiers[parameter.name] = result.type;
      }
    }
  }

  async handleSharedWithChanged(is_shared_changed: boolean) {
    await new Promise(resolve => setTimeout(resolve)); // wait for value to commit.

    const projectIdInput = this.querySelector<HTMLInputElement>(
      '[name="project_id"]:checked'
    );
    if (!(projectIdInput instanceof HTMLInputElement)) {
      throw new Error('Expected project_id input element');
    }

    const isSharedInput = this.querySelector<HTMLInputElement>(
      '[name^="is_shared:"]:checked'
    );
    if (!(isSharedInput instanceof HTMLInputElement)) {
      throw new Error('Expected is_shared input element');
    }
    const is_shared = +isSharedInput.value as ReportSharedWith;
    this.sharesFormGroup.hidden =
      is_shared !== ReportSharedWith.SpecificOrganizations &&
      is_shared !== ReportSharedWith.SpecificUsers;
    const box = this.sharesFormGroup.querySelector('autocomplete-list-box');
    if (!box) {
      throw new Error('Expected autocomplete-list-box element');
    }
    if (is_shared_changed) {
      box.clear();
    }
    if (!this.sharesFormGroup.hidden) {
      this.sharesFormGroup.querySelector('.form-label')!.textContent =
        `Shared with ${is_shared === ReportSharedWith.SpecificOrganizations ? 'organizations' : 'users'}`;
      const [path] = box.autocomplete.src.split('?');
      box.autocomplete.src = `${path}?with=${is_shared}${projectIdInput.value ? `&project_id=${projectIdInput.value}` : ''}`;
    }
  }
}

function updateColumnSortOptions(
  target: HTMLSelectElement,
  selects: HTMLSelectElement[]
) {
  const max =
    Math.max(...selects.map(x => +(x.value[0] ?? '-1'))) +
    (target.value ? 1 : 2);
  for (const option of target.options) {
    option.disabled = option.value > max.toString();
  }
}

declare global {
  interface Window {
    ReportConfigurationElement: typeof ReportConfigurationElement;
  }
  interface HTMLElementTagNameMap {
    'report-configuration': ReportConfigurationElement;
  }
}
